/**
 * Relation identity for the IntelliSense FK / loose-FK JOIN emitters: which
 * snapshot table an FK edge's endpoint, a MetaInfo declaration's table, or a
 * bound relation of the query actually IS.
 *
 * FK edges carry CATALOG spellings, MetaInfo declarations whatever the user
 * (or an import) wrote. PostgreSQL / Oracle quoted names and MySQL with
 * `lower_case_table_names = 0` keep `orders` and `Orders` as two tables, so an
 * endpoint names a table by its EXACT spelling first — schema, then name — and
 * falls back to the case-insensitive match only when that is unambiguous (one
 * spelling). A folded compare alone gave `"Orders"` the FKs of `orders` (and
 * the per-candidate dedupe dropped `orders` itself). An endpoint without a
 * schema (SQLite, MySQL, legacy declarations) names that table in any schema.
 *
 * A bound relation is resolved by the dialect resolver's own rule for an
 * unqualified name (PostgreSQL search_path, SQL Server default schema then
 * `dbo`, …) — `resolveBinding` — so `FROM orders o JOIN ▮` follows the FKs of
 * the table the query reads, never another schema's same-named one.
 *
 * Cost: the tables are indexed once per matcher by folded `(schema, name)`
 * (plus folded name for the schema-less and any-schema lookups), so resolving
 * an endpoint reads only its own schema's same-named bucket — not every
 * schema's table of that name (1,000 tenant schemas each holding `orders`
 * made each `orders` endpoint a 1,000-table scan: `tenantSchemaFkJoin.bench.ts`).
 * Endpoints are memoised per `(schema, name)` without building string keys,
 * since the emitters ask `endpointIs` once per (candidate × edge) pair.
 *
 * Built for ONE synchronous completion pass (memoised; the snapshot arrays may
 * be refilled in place between passes — see `fkEdgeIndex.ts` — so the index is
 * never cached on the array across passes; building it is O(tables)).
 */

import type { AliasBinding } from '@/core/engines';

/** The identity part of a snapshot table. */
export type EndpointTable = { readonly name: string; readonly schema?: string };

export type FkEndpointMatcher<T extends EndpointTable = EndpointTable> = {
  /** The snapshot table `binding` names under the engine's rules, or undefined. */
  readonly bindingTable: (binding: AliasBinding) => T | undefined;
  /** The snapshot tables an endpoint `(schema, name)` names (see the header). */
  readonly endpointTables: (schema: string | undefined, name: string) => readonly T[];
  /** Whether the endpoint `(schema, name)` names `table` (exact identity). */
  readonly endpointIs: (schema: string | undefined, name: string, table: EndpointTable) => boolean;
};

/** Exact identity key of a table — spelling kept, schema-less distinct from any schema. */
export function endpointTableKey(t: EndpointTable): string {
  return `${t.schema === undefined ? '\u0001' : t.schema}\u0000${t.name}`;
}

/** Exact spellings first; else the whole case-insensitive set when it holds one spelling. */
function exactElseOnlyFold<T>(items: readonly T[], spelling: (t: T) => string, wanted: string): readonly T[] {
  const exact = items.filter((t) => spelling(t) === wanted);
  if (exact.length > 0) return exact;
  return new Set(items.map(spelling)).size === 1 ? items : [];
}

type Resolved<T> = { readonly tables: readonly T[]; readonly keys: ReadonlySet<string> | undefined };

/** Above this many tables an endpoint's identity test reads a key set instead of the list. */
const LINEAR_IDENTITY_MAX = 4;
const NONE: readonly never[] = [];
/** Memo slot for an endpoint without a schema (no schema spelling can equal it). */
const ANY_SCHEMA = '\u0001';

function push<K, V>(map: Map<K, V[]>, key: K, value: V): void {
  const bucket = map.get(key);
  if (bucket) bucket.push(value);
  else map.set(key, [value]);
}

export function makeFkEndpointMatcher<T extends EndpointTable>(
  tables: readonly T[],
  resolveBinding: (binding: AliasBinding) => T | undefined,
): FkEndpointMatcher<T> {
  // Folded name → every table of that name (any-schema endpoints); folded
  // schema → folded name → that schema's tables; folded name → the tables
  // without a schema. Each bucket keeps the snapshot order.
  const byFoldedName = new Map<string, T[]>();
  const bySchemaThenName = new Map<string, Map<string, T[]>>();
  const schemalessByName = new Map<string, T[]>();
  for (const t of tables) {
    const name = t.name.toLowerCase();
    push(byFoldedName, name, t);
    if (!t.schema) {
      push(schemalessByName, name, t);
      continue;
    }
    const schema = t.schema.toLowerCase();
    let names = bySchemaThenName.get(schema);
    if (!names) bySchemaThenName.set(schema, (names = new Map()));
    push(names, name, t);
  }

  const compute = (s: string | undefined, name: string): Resolved<T> => {
    const folded = name.toLowerCase();
    let inSchema: readonly T[];
    if (s === undefined) inSchema = byFoldedName.get(folded) ?? NONE;
    else {
      const withSchema = bySchemaThenName.get(s.toLowerCase())?.get(folded) ?? NONE;
      inSchema = [...exactElseOnlyFold(withSchema, (t) => t.schema!, s), ...(schemalessByName.get(folded) ?? NONE)];
    }
    // Per schema spelling, the name: exact first, else the only fold.
    const bySchema = new Map<string, T[]>();
    for (const t of inSchema) push(bySchema, t.schema ?? ANY_SCHEMA, t);
    const out: T[] = [];
    for (const group of bySchema.values()) out.push(...exactElseOnlyFold(group, (t) => t.name, name));
    return { tables: out, keys: out.length > LINEAR_IDENTITY_MAX ? new Set(out.map(endpointTableKey)) : undefined };
  };

  // schema (ANY_SCHEMA for none) → name → resolution.
  const resolved = new Map<string, Map<string, Resolved<T>>>();
  const resolve = (schema: string | undefined, name: string): Resolved<T> => {
    const s = schema === '' ? undefined : schema;
    let byName = resolved.get(s ?? ANY_SCHEMA);
    if (!byName) resolved.set(s ?? ANY_SCHEMA, (byName = new Map()));
    let hit = byName.get(name);
    if (!hit) byName.set(name, (hit = compute(s, name)));
    return hit;
  };

  const bound = new Map<AliasBinding, T | undefined>();
  return {
    bindingTable: (binding) => {
      if (!bound.has(binding)) bound.set(binding, resolveBinding(binding));
      return bound.get(binding);
    },
    endpointTables: (schema, name) => resolve(schema, name).tables,
    endpointIs: (schema, name, table) => {
      const { tables: named, keys } = resolve(schema, name);
      if (keys) return keys.has(endpointTableKey(table));
      return named.some((t) => t.name === table.name && t.schema === table.schema);
    },
  };
}
